import { CommonModule, Location } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { CdkDrag, CdkDragDrop, CdkDropList, DragDropModule } from "@angular/cdk/drag-drop";
import { CdkScrollableModule } from "@angular/cdk/scrolling";
import { MatTooltipModule } from "@angular/material/tooltip";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription } from "rxjs";
import Swal from "sweetalert2";

import { SolicitudesIaService } from "../solicitudes-ia.service";
import { MatrizRiesgoService } from "../../matriz-riesgos-ia/matriz-riesgo.service";
import { InventarioIAService } from "../../inventario-ia/inventario-ia.service";
import { ModalDarComponent } from "../modal-dar/modal-dar.component";
import {
  COLUMNAS_SIA,
  ESTATUS_SIA,
  EstatusSolicitudIA,
  ORIGEN_SOLICITUD_IA,
  ORIGEN_SOLICITUD_IA_EVAL,
  SolicitudIA,
  SolicitudIADar,
  SolicitudIAHistorial,
} from "../../shared/model/iso42001/solicitud-ia.model";
import { MatrizEvidencia } from "../../shared/model/iso42001/matriz-riesgo.model";

/**
 * El tablero se mueve arrastrando con el mouse cuando no cabe: a lo ancho,
 * si la ventana es angosta, y a lo alto dentro de la columna donde se
 * empezo, si trae mas tarjetas de las que se ven. Pedido de Norman
 * (17/09/2026): "si se hace pequena la pestana, pon la manita para
 * moverse; solo aparece scroll".
 *
 * Un clic sin arrastre sigue abriendo la tarjeta: solo pasados 5 px cuenta
 * como arrastre, y entonces el clic de ese soltar se descarta. Solo mouse:
 * en pantallas tactiles el dedo ya desplaza solo. Corre fuera de Angular
 * (clases directo en el DOM) para no disparar deteccion de cambios en cada
 * movimiento. Devuelve con que quitar los escuchas.
 */
function arrastraParaMover(tablero: HTMLElement): () => void {
  const UMBRAL = 5;
  let inicio: {
    id: number;
    x: number;
    y: number;
    left: number;
    top: number;
    cuerpo: HTMLElement | null;
  } | null = null;
  let moviendo = false;
  let tragarClic = false;

  const cuerpoBajo = (el: EventTarget | null): HTMLElement | null =>
    el instanceof Element ? (el.closest(".gia-col__cuerpo") as HTMLElement | null) : null;
  const desbordaX = () => tablero.scrollWidth > tablero.clientWidth + 1;
  const desbordaY = (c: HTMLElement | null) => !!c && c.scrollHeight > c.clientHeight + 1;

  const alPasar = (e: PointerEvent) => {
    if (inicio) {
      return;
    }
    const puede = e.pointerType === "mouse" && (desbordaX() || desbordaY(cuerpoBajo(e.target)));
    tablero.classList.toggle("se-puede-mover", puede);
  };

  const alBajar = (e: PointerEvent) => {
    if (e.pointerType !== "mouse" || e.button !== 0) {
      return;
    }
    const t = e.target as Element;
    // Una tarjeta que se puede arrastrar de columna es del CDK, no del
    // paneo: si los dos reaccionaran al mismo pointerdown, el tablero se
    // desplazaria mientras la tarjeta vuela.
    if (t.closest("input, select, textarea, a, .gia-col__pager, .gia-card.es-arrastrable")) {
      return;
    }
    // Sobre la barra de scroll del tablero: que la maneje el navegador.
    const r = tablero.getBoundingClientRect();
    if (e.clientY > r.top + tablero.clientTop + tablero.clientHeight) {
      return;
    }
    const cuerpo = cuerpoBajo(t);
    if (!desbordaX() && !desbordaY(cuerpo)) {
      return;
    }
    inicio = {
      id: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      left: tablero.scrollLeft,
      top: cuerpo ? cuerpo.scrollTop : 0,
      cuerpo: desbordaY(cuerpo) ? cuerpo : null,
    };
    moviendo = false;
  };

  const alMover = (e: PointerEvent) => {
    if (!inicio || e.pointerId !== inicio.id) {
      alPasar(e);
      return;
    }
    const dx = e.clientX - inicio.x;
    const dy = e.clientY - inicio.y;
    if (!moviendo) {
      if (Math.abs(dx) < UMBRAL && Math.abs(dy) < UMBRAL) {
        return;
      }
      moviendo = true;
      tablero.classList.add("arrastrando");
      try {
        tablero.setPointerCapture(e.pointerId);
      } catch {
        // Si el navegador no deja capturar, se mueve igual mientras el
        // puntero siga encima.
      }
    }
    e.preventDefault();
    tablero.scrollLeft = inicio.left - dx;
    if (inicio.cuerpo) {
      inicio.cuerpo.scrollTop = inicio.top - dy;
    }
  };

  const alSoltar = (e: PointerEvent) => {
    if (!inicio || e.pointerId !== inicio.id) {
      return;
    }
    if (moviendo) {
      tragarClic = true;
      // Si el soltar no produce clic (se solto fuera), que no se trague el
      // siguiente clic de verdad.
      setTimeout(() => (tragarClic = false), 0);
      tablero.classList.remove("arrastrando");
      try {
        tablero.releasePointerCapture(e.pointerId);
      } catch {
        // ya estaba suelto
      }
    }
    inicio = null;
    moviendo = false;
  };

  const alClic = (e: MouseEvent) => {
    if (tragarClic) {
      tragarClic = false;
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const alSalir = () => {
    if (!inicio) {
      tablero.classList.remove("se-puede-mover");
    }
  };

  tablero.addEventListener("pointermove", alMover);
  tablero.addEventListener("pointerdown", alBajar);
  tablero.addEventListener("pointerup", alSoltar);
  tablero.addEventListener("pointercancel", alSoltar);
  tablero.addEventListener("pointerleave", alSalir);
  tablero.addEventListener("click", alClic, true);
  return () => {
    tablero.removeEventListener("pointermove", alMover);
    tablero.removeEventListener("pointerdown", alBajar);
    tablero.removeEventListener("pointerup", alSoltar);
    tablero.removeEventListener("pointercancel", alSoltar);
    tablero.removeEventListener("pointerleave", alSalir);
    tablero.removeEventListener("click", alClic, true);
    tablero.classList.remove("se-puede-mover", "arrastrando");
  };
}

/**
 * Un aviso flotante de solicitud nueva.
 *
 * Vive solo en pantalla: no se guarda en ningun lado. Es un recordatorio
 * de que algo entro mientras el tablero estaba abierto, no un registro.
 */
interface Aviso {
  idSolicitudIa: number;
  folio: string;
  nombreIa: string;
  solicitante: string;
  /** El temporizador que lo retira solo. Se limpia al cerrarlo a mano. */
  reloj: any;
}

/** Para la fecha corta de la tarjeta. */
const MESES = ["ene", "feb", "mar", "abr", "may", "jun",
               "jul", "ago", "sep", "oct", "nov", "dic"];

/** Una columna del tablero, ya con lo suyo dentro. */
interface Columna {
  titulo: string;
  icono: string;
  color: string;
  pista: string;
  vacia: string;
  /** Los estatus que caen aquí; el primero es al que lleva soltar una tarjeta. */
  estatus: EstatusSolicitudIA[];
  /** Todas las de esta columna. */
  solicitudes: SolicitudIA[];
  /** En qué página va. Cada columna la suya: se despachan por separado. */
  pagina: number;
}

/**
 * La bandeja de los cinco que atienden las solicitudes de IA.
 *
 * Lista, detalle y acciones en una sola pantalla: el detalle se abre a un
 * lado, no en otra ruta, porque lo que se hace aquí es despachar una cola
 * y salir de la lista en cada solicitud rompe el ritmo.
 *
 * El servidor revalida TODO lo que esta pantalla decide mostrar. Que un
 * botón no se pinte no es una restricción de seguridad: es comodidad.
 */
@Component({
  selector: "app-gestion-solicitudes-ia",
  standalone: true,
  templateUrl: "./gestion-solicitudes-ia.component.html",
  styleUrls: ["./gestion-solicitudes-ia.component.css"],
  imports: [
    CommonModule,
    FormsModule,
    DragDropModule,
    // cdkScrollable en el tablero: el CDK sólo desplaza solo los
    // contenedores que conoce (getAncestorScrollContainers), y sin esto,
    // arrastrando con la ventana angosta, las columnas que quedan fuera de
    // vista no se podían alcanzar.
    CdkScrollableModule,
    MatTooltipModule,
    ModalDarComponent,
  ],
})
export class GestionSolicitudesIaComponent implements OnInit, OnDestroy {
  readonly ESTATUS = ESTATUS_SIA;

  cargando = false;
  solicitudes: SolicitudIA[] = [];
  /** El tablero: las cinco columnas, con sus solicitudes repartidas. */
  columnas: Columna[] = [];
  totalItems = 0;
  responsables: SolicitudIA[] = [];
  error = "";

  /* ---------------- Filtros y paginado ---------------- */
  busca = "";
  filtroEstatus = "";
  filtroResponsable: number | null = null;
  /**
   * El tablero trae TODO de una vez: repartir en columnas y paginar a la
   * vez no tiene sentido —la pagina 2 dejaria columnas mintiendo— y este
   * modulo no va a tener miles de solicitudes. Cada columna se desliza
   * por dentro si le sobran tarjetas.
   */
  private readonly TODAS = 500;

  /* ---------------- El detalle abierto ---------------- */
  abierta: SolicitudIA | null = null;
  historial: SolicitudIAHistorial[] = [];
  cargandoDetalle = false;
  trabajando = false;

  /** Los archivos de la solicitud abierta. */
  archivos: any[] = [];

  /* ---------------- El DAR capturado ----------------
     Si quien pidió no subió el Excel y lo llenó en pantalla, aquí se
     lee. Va en SOLO LECTURA: el evaluador lo consulta, no lo reescribe. */
  dar: SolicitudIADar | null = null;

  /**
   * Como fue la consulta del DAR y la de los archivos.
   *
   * Antes las dos guardaban null al fallar, y la pantalla decia "No
   * adjunto el DAR ni lo lleno en pantalla": la misma frase para "no hay"
   * y para "no se pudo preguntar". Con esa frase delante se rechaza por
   * falta de justificacion una solicitud que si la traia.
   */
  darCarga: "cargando" | "listo" | "falla" = "listo";
  archivosCarga: "cargando" | "listo" | "falla" = "listo";
  darAbierto = false;

  /** El comentario que se está escribiendo. */
  comentario = "";

  /* ---------------- La evidencia de la evaluación ----------------
     Lo que sube quien atiende la solicitud para sustentar su dictamen
     (pedido de Norman, 23/09/2026). Vive en el almacén de evidencias con su
     propio origen, SOLICITUD_IA_EVAL, aparte del DAR de quien pidió. La ven
     quien la pidió y los cinco; la sube y la quita SÓLO el responsable, y
     sólo mientras la solicitud está en evaluación o esperando información:
     después del dictamen queda cerrada (el servidor lo exige igual). */
  evidenciasEval: MatrizEvidencia[] = [];
  evidenciaCarga: "listo" | "cargando" | "falla" = "listo";
  /**
   * Qué solicitudes están subiendo evidencia AHORA. Por solicitud y no una
   * bandera suelta: si se sale de una a media subida y se abre otra, la
   * otra no debe quedarse diciendo «Subiendo…» (hallazgo de la revisión).
   */
  private subiendoEn = new Set<number>();

  /** Si la solicitud abierta está subiendo evidencia. */
  get subiendoEvidencia(): boolean {
    return !!this.abierta && this.abierta.idSolicitudIa != null && this.subiendoEn.has(this.abierta.idSolicitudIa);
  }

  /**
   * Los archivos elegidos con el clip y todavía sin enviar, por solicitud:
   * igual que el texto (borradores), salir de la solicitud ya no los tira.
   * Antes el texto volvía y los archivos no, y el comentario podía acabar
   * hablando de un archivo que nunca se subió.
   */
  private adjuntosPorSol = new Map<number, File[]>();

  /** Lo que acepta el almacén (MatrizEvidenciaBuss.EXTENSIONES_PERMITIDAS). */
  readonly EVIDENCIA_EXT =
    ".pdf,.jpg,.jpeg,.png,.gif,.webp,.bmp,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.zip,.mp4,.msg,.eml";
  /** El mismo tope que la pantalla de quien pide: el servidor no lo revisa. */
  private readonly EVIDENCIA_MAX = 10 * 1024 * 1024;

  /** Áreas del catálogo ISO, para aprobar. NO son las de adm_area. */
  catAreas: Array<{ id?: number; area?: string }> = [];

  /* ================= El tablero, al dia solo =================

     No hay boton de Actualizar: el tablero se consulta cada poco tiempo
     y se pinta lo que haya cambiado. Un boton obliga a acordarse de
     pulsarlo, y en una bandeja que se reparten cinco personas eso
     significa trabajar sobre una foto vieja.

     Tres cuidados, y los tres importan:

     1. La consulta de fondo va SIN el spinner de pantalla completa. El
        interceptor lo saca en cuanto una peticion pasa de 100 ms, asi
        que sin esto la app parpadearia sola cada medio minuto.
     2. No toca `cargando`. Ese campo esconde el tablero mientras carga,
        y un refresco de fondo que lo encendiera haria desaparecer las
        columnas un instante cada vez.
     3. El temporizador vive FUERA de la zona de Angular. Dentro,
        despertaria una deteccion de cambios de toda la aplicacion en
        cada vuelta aunque no hubiera nada que repintar. */

  /** Cada cuanto se vuelve a preguntar. */
  private readonly LATIDO_MS = 25000;

  private latido: any = null;
  /** Para no encimar dos consultas si el servidor va lento. */
  private enFondo = false;
  private destruido = false;

  /** Verdadero mientras la ultima consulta de fondo haya fallado. */
  sinConexion = false;

  /**
   * Distinto de sinConexion: el servidor contesto, y contesto que no.
   * Reintentar cada 25 segundos no lo va a arreglar, asi que el latido se
   * apaga y la etiqueta lo dice con su nombre.
   */
  sinPermiso = false;

  /**
   * El servidor contesto AUTH-001: no hay sesion.
   *
   * Es un caso REAL y frecuente, no una rareza: SolicitudIAService lo
   * devuelve cada vez que la HttpSession se cae, y eso pasa en cada
   * despliegue y cada vez que caduca. Como viaja en un 200 con
   * codigoError —en CosRest no hay 401, FilterSeguridad esta comentado
   * entero—, el interceptor de errores ni se entera: lo tiene que ver la
   * pantalla. Antes caia en el else y se pintaba "Sin conexión", que
   * manda a revisar la red cuando lo que hay que hacer es volver a
   * entrar; y el latido seguia preguntando cada 25 segundos para siempre.
   */
  sesionCaida = false;
  /** Los folios que ya se habian visto, para saber cual es nueva. */
  private vistas = new Set<number>();

  /* ---------------- Los avisos flotantes ----------------

     Cuando entra una solicitud nueva sale un aviso arriba a la derecha.
     Dura dos minutos, se puede cerrar antes, y se va solo en cuanto
     alguien la toma: un aviso de algo que ya se esta atendiendo es
     ruido, y peor aun, hace que dos personas vayan por lo mismo.

     Solo mientras el tablero este abierto. Un aviso de verdad —correo o
     campanita— necesita que el servidor empuje, y el canal STOMP que el
     proyecto tenia se vacio hace meses. */

  avisos: Aviso[] = [];

  /** Dos minutos, que es lo que se pidio. */
  private readonly AVISO_MS = 2 * 60 * 1000;

  /**
   * Si todavia no se sabe que habia.
   *
   * NO vale mirar si `vistas` esta vacio: eso confunde "no se todavia"
   * con "se que no habia nada", y en un tablero vacio —el caso para el
   * que existe el aviso— la primera solicitud del dia no se anunciaria.
   */
  private primeraVez = true;

  /**
   * Los filtros con los que se consulto A PROPOSITO la ultima vez.
   *
   * El buscador es [(ngModel)] pero solo se aplica al pulsar Enter. Si
   * el latido leyera `busca` en vivo, un texto a medio escribir filtraria
   * el tablero solo a los pocos segundos, y borrarlo sin pulsar Enter
   * haria que TODAS las solicitudes parecieran nuevas.
   */
  private filtrosVivos: { busca: string; estatus: string; responsable: number | null } = {
    busca: "",
    estatus: "",
    responsable: null,
  };

  /**
   * Sello de la ultima consulta pedida. Una respuesta que llega tarde,
   * cuando ya se pidio otra, no pinta: si no, una consulta de fondo
   * lenta puede sobreescribir el resultado del filtro que el usuario
   * acaba de aplicar.
   */
  private sello = 0;

  /** Para no prometer en pantalla un refresco que ya se apago. */
  get latidoVivo(): boolean {
    return !!this.latido;
  }

  /** Quita los escuchas del arrastre del tablero; ver arrastraParaMover. */
  private quitaArrastre: (() => void) | null = null;

  /**
   * El tablero entra y sale con *ngIf (mientras carga no esta): cada vez
   * que aparece se le pone el arrastre, y al irse se le quita.
   */
  /** El tablero en el DOM, para la clase del cursor mientras se arrastra. */
  private tablero: HTMLElement | null = null;

  @ViewChild("tablero")
  set tableroRef(ref: ElementRef<HTMLElement> | undefined) {
    if (this.quitaArrastre) {
      this.quitaArrastre();
      this.quitaArrastre = null;
    }
    this.tablero = ref ? ref.nativeElement : null;
    if (ref) {
      this.quitaArrastre = this.zone.runOutsideAngular(() => arrastraParaMover(ref.nativeElement));
    }
  }

  /** La caja de la historia, la que se desliza. */
  private historiaCaja: HTMLElement | null = null;

  /** El título del detalle: recibe el foco al abrir una solicitud. */
  private detalleTitulo: HTMLElement | null = null;

  @ViewChild("detalleTitulo")
  set detalleTituloRef(ref: ElementRef<HTMLElement> | undefined) {
    this.detalleTitulo = ref ? ref.nativeElement : null;
  }

  /**
   * Si el detalle se abrió desde el tablero de esta misma pantalla: detrás,
   * en el historial del navegador, está el tablero, y Regresar es "Atrás".
   */
  private desdeTablero = false;

  /** Dónde estaba el tablero al abrir (guardaPosicion / reponPosicion). */
  private posicion: { idSolicitud: number; tableroX: number; columnas: number[] } | null = null;

  /** La suscripción a la dirección (?solicitud=). */
  private subUrl: Subscription | null = null;

  /**
   * Lo escrito en "Escribe un comentario…" y no enviado, por solicitud.
   * Salir de la solicitud (Regresar, Atrás, un aviso, el menú lateral) ya
   * no lo tira: al volver a ella sigue ahí.
   */
  private borradores = new Map<number, string>();

  @ViewChild("historiaCaja")
  set historiaCajaRef(ref: ElementRef<HTMLElement> | undefined) {
    this.historiaCaja = ref ? ref.nativeElement : null;
  }

  /**
   * Deja la historia hasta abajo.
   *
   * La historia va del primer movimiento al último, así que lo que se
   * busca al abrir una solicitud —y lo que uno acaba de escribir— está al
   * final. Se llama después de detectChanges(), con los renglones ya
   * pintados. Si no hay de dónde rodar, no hace nada.
   */
  private alFinalDeLaHistoria(suave = false): void {
    const caja = this.historiaCaja;
    if (!caja) {
      return;
    }
    const quieto = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    caja.scrollTo({ top: caja.scrollHeight, behavior: suave && !quieto ? "smooth" : "auto" });
  }

  constructor(
    private service: SolicitudesIaService,
    private evidencias: MatrizRiesgoService,
    private inventario: InventarioIAService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private host: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef,
    private zone: NgZone,
  ) {}

  ngOnInit(): void {
    this.leeQuienSoy();
    this.cargar();
    this.cargaCatAreas();
    this.arrancaLatido();
    // La dirección dice qué solicitud está abierta (ver siguelaUrl). La
    // primera vez contesta en seguida: si se entró con ?solicitud=42, se abre.
    this.subUrl = this.route.queryParamMap.subscribe((p) => this.siguelaUrl(p.get("solicitud")));
  }

  /**
   * El catalogo de areas ISO, que es con el que la IA entra al inventario.
   *
   * Aparte, para poder reintentarlo: sin este catalogo no se puede
   * autorizar nada, y hasta ahora un fallo aqui se quedaba callado hasta
   * que alguien abria el dialogo de autorizar y se encontraba el select
   * vacio y sin salida.
   */
  private cargaCatAreas(): void {
    this.inventario.catAreas().subscribe({
      next: (res: any) => {
        this.catAreas = (res && (res.areas || res.catAreas || res.lista)) || [];
        this.cdr.detectChanges();
      },
      error: () => {
        this.catAreas = [];
        this.cdr.detectChanges();
      },
    });
  }

  ngOnDestroy(): void {
    this.destruido = true;
    if (this.subUrl) {
      this.subUrl.unsubscribe();
      this.subUrl = null;
    }
    if (this.quitaArrastre) {
      this.quitaArrastre();
      this.quitaArrastre = null;
    }
    this.paraLatido();
    clearTimeout(this.relojArrastre);
    // Los relojes de los avisos: sin esto siguen corriendo
    // despues de salir de la pantalla y despiertan una vista muerta.
    this.avisos.forEach((a) => clearTimeout(a.reloj));
    this.avisos = [];
    document.removeEventListener("visibilitychange", this.alVolver);
  }

  private arrancaLatido(): void {
    document.addEventListener("visibilitychange", this.alVolver);
    this.zone.runOutsideAngular(() => {
      this.latido = setInterval(() => this.refrescaFondo(), this.LATIDO_MS);
    });
  }

  private paraLatido(): void {
    if (this.latido) {
      clearInterval(this.latido);
      this.latido = null;
    }
  }

  /**
   * Al volver a la pestana se refresca de inmediato.
   *
   * Mientras la pestana esta escondida el navegador estira los
   * temporizadores, asi que al regresar lo que hay en pantalla puede
   * llevar minutos parado. Es el momento en que mas se nota.
   */
  private alVolver = (): void => {
    // Solo si el latido sigue vivo. Apagarlo por un 401 y seguir
    // preguntando cada vez que el usuario vuelve a la pestana seria
    // apagarlo a medias.
    if (!document.hidden && this.latido) {
      this.refrescaFondo();
    }
  };

  /**
   * La consulta de fondo. Se salta sola cuando no tiene sentido hacerla:
   * con la pestana escondida no hay nadie mirando, y en mitad de una
   * accion el resultado se va a recargar igualmente al terminar.
   */
  private refrescaFondo(): void {
    // Tambien se salta durante una carga en primer plano: esa acaba de
    // pedirla el usuario y va a repintar igual.
    // Y mientras una tarjeta va en el aire: repartir las columnas de nuevo
    // destruiría la que se está arrastrando, el CDK no llegaría a avisar
    // que terminó y el tablero se quedaría creyendo que sigue el arrastre.
    if (
      this.destruido ||
      this.enFondo ||
      this.trabajando ||
      this.cargando ||
      this.arrastrando ||
      document.hidden
    ) {
      return;
    }
    this.enFondo = true;
    const mio = ++this.sello;
    this.service
      .bandeja(
        {
          // Los filtros CONFIRMADOS, no lo que haya en el buscador ahora
          // mismo a medio escribir.
          busca: this.filtrosVivos.busca || undefined,
          filtroEstatus: this.filtrosVivos.estatus || undefined,
          filtroResponsable: this.filtrosVivos.responsable,
          incluyeCanceladas: true,
          pagina: 1,
          porPagina: this.TODAS,
        },
        true,
      )
      .subscribe({
        next: (res) => {
          this.enFondo = false;
          // Llego tarde: mientras tanto se pidio otra cosa.
          if (this.destruido || mio !== this.sello) {
            return;
          }
          // Este servidor contesta 200 con codigoError para los fallos
          // de negocio. Tragarselo dejaria el tablero congelado con el
          // punto verde diciendo que esta al dia.
          if (res.codigoError) {
            // Sin permiso no se insiste: preguntar cada 25 segundos algo
            // que ya contestaron que no es golpear al servidor para nada,
            // y la etiqueta "Sin conexion" manda a revisar la red cuando
            // lo que hay que revisar es el perfil.
            if (res.codigoError === "AUTH-001") {
              this.sesionCaida = true;
              this.paraLatido();
              this.error =
                "Tu sesión caducó. Vuelve a entrar para seguir trabajando.";
            } else if (res.codigoError === "VAL-PERFIL") {
              this.sinPermiso = true;
              this.paraLatido();
              this.sinPermisoSinDetalle();
              // Como en cargar(): sin permiso no quedan tarjetas ni avisos
              // que se puedan picar.
              this.solicitudes = [];
              this.reparte();
              this.avisos.forEach((a) => clearTimeout(a.reloj));
              this.avisos = [];
              this.error =
                res.mensajeError ||
                "Ya no tienes permiso para atender solicitudes de IA.";
            } else {
              this.sinConexion = true;
            }
            this.cdr.detectChanges();
            return;
          }
          this.sinConexion = false;
          this.sinPermiso = false;
          this.sesionCaida = false;
          // Si se entro con la red caida, el aviso rojo se quedaba
          // clavado encima de un tablero ya correcto.
          this.error = "";
          this.acusaNuevas(res.solicitudes || []);
          this.solicitudes = res.solicitudes || [];
          this.totalItems = res.totalItems || 0;
          this.responsables = res.responsables || [];
          this.reparte();
          this.cdr.detectChanges();
        },
        error: (e: any) => {
          this.enFondo = false;
          if (this.destruido || mio !== this.sello) {
            return;
          }
          // Con la sesion caida no tiene sentido seguir preguntando cada
          // 25 segundos. Se apaga el latido y ya esta: cerrar la sesion
          // aqui recargaria la pagina encima de lo que el usuario tenga
          // a medio escribir. De eso se encarga su siguiente accion.
          if (e && (e.status === 401 || e.status === 403)) {
            this.paraLatido();
          }
          // No se borra lo que ya esta en pantalla: una consulta que
          // falla no significa que la bandeja se haya vaciado.
          this.sinConexion = true;
          this.cdr.detectChanges();
        },
      });
  }

  /** Cuenta las que no estaban la vez pasada y lo avisa un rato. */
  private acusaNuevas(llegaron: SolicitudIA[]): void {
    // Los avisos de lo que ya se esta atendiendo se retiran SIEMPRE,
    // incluso en la primera vuelta.
    this.retiraAtendidos(llegaron);

    if (this.primeraVez) {
      this.recuerda(llegaron);
      return;
    }

    const nuevas = llegaron.filter(
      (s) => s.idSolicitudIa != null && !this.vistas.has(s.idSolicitudIa),
    );
    this.recuerda(llegaron);
    if (!nuevas.length) {
      return;
    }

    // Solo se avisa de las que esperan a alguien. Una que entra ya
    // tomada —no pasa hoy, pero podria— no necesita que nadie corra.
    nuevas.filter((s) => this.esperaAAlguien(s)).forEach((s) => this.sacaAviso(s));
  }

  /** Si todavia no la ha tomado nadie. */
  private esperaAAlguien(s: SolicitudIA): boolean {
    return s.estatus === "SOLICITADA" && !s.idUsuarioResponsable;
  }

  /**
   * Saca el aviso de una solicitud, y programa su retirada.
   *
   * El reloj corre FUERA de la zona de Angular, como el latido: dentro,
   * cada aviso despertaria una deteccion de cambios de toda la
   * aplicacion durante dos minutos sin que hubiera nada que repintar.
   */
  private sacaAviso(s: SolicitudIA): void {
    const id = s.idSolicitudIa;
    if (id == null || this.avisos.some((a) => a.idSolicitudIa === id)) {
      return;
    }
    const aviso: Aviso = {
      idSolicitudIa: id,
      folio: s.folio || "",
      nombreIa: s.nombreIa || "",
      solicitante: s.solicitante || "",
      reloj: null,
    };
    this.avisos = [aviso, ...this.avisos];
    this.zone.runOutsideAngular(() => {
      aviso.reloj = setTimeout(() => this.quitaAviso(id, true), this.AVISO_MS);
    });
    this.cdr.detectChanges();
  }

  /** Cerrarlo a mano. */
  cierraAviso(a: Aviso): void {
    this.quitaAviso(a.idSolicitudIa, false);
  }

  private quitaAviso(id: number, deReloj: boolean): void {
    const fuera = this.avisos.find((a) => a.idSolicitudIa === id);
    if (!fuera) {
      return;
    }
    clearTimeout(fuera.reloj);
    this.avisos = this.avisos.filter((a) => a.idSolicitudIa !== id);
    if (!this.destruido) {
      this.cdr.detectChanges();
    }
  }

  /**
   * Retira los avisos de lo que ya se atendio.
   *
   * Es lo que evita que dos personas vayan por la misma solicitud: en
   * cuanto una la toma, el aviso desaparece de la pantalla de las otras
   * en el siguiente latido.
   */
  private retiraAtendidos(llegaron: SolicitudIA[]): void {
    if (!this.avisos.length) {
      return;
    }
    const esperando = new Set(
      llegaron
        .filter((s) => this.esperaAAlguien(s))
        .map((s) => s.idSolicitudIa)
        .filter((id): id is number => id != null),
    );
    this.avisos
      .filter((a) => !esperando.has(a.idSolicitudIa))
      .forEach((a) => this.quitaAviso(a.idSolicitudIa, false));
  }

  /** Ir a la solicitud desde su aviso. */
  abreDesdeAviso(a: Aviso): void {
    const s = this.solicitudes.find((x) => x.idSolicitudIa === a.idSolicitudIa);
    this.quitaAviso(a.idSolicitudIa, false);
    if (s) {
      this.abre(s);
    }
  }

  trackAviso = (_: number, a: Aviso) => a.idSolicitudIa;

  private recuerda(lista: SolicitudIA[]): void {
    this.primeraVez = false;
    this.vistas = new Set(
      lista.map((s) => s.idSolicitudIa).filter((id): id is number => id != null),
    );
  }

  /* ================= La lista ================= */

  cargar(): void {
    this.cargando = true;
    this.error = "";
    // Estos son, a partir de ahora, los filtros de verdad: los que el
    // latido va a repetir.
    this.filtrosVivos = {
      busca: this.busca.trim(),
      estatus: this.filtroEstatus,
      responsable: this.filtroResponsable,
    };
    const mio = ++this.sello;
    this.service
      .bandeja({
        busca: this.filtrosVivos.busca || undefined,
        filtroEstatus: this.filtrosVivos.estatus || undefined,
        filtroResponsable: this.filtrosVivos.responsable,
        // Siempre: la columna "Cerradas" las necesita.
        incluyeCanceladas: true,
        pagina: 1,
        porPagina: this.TODAS,
      })
      .subscribe({
        next: (res) => {
          this.cargando = false;
          if (mio !== this.sello) {
            return;
          }
          if (res.codigoError) {
            if (res.codigoError === "AUTH-001") {
              this.sesionCaida = true;
              this.paraLatido();
            }
            if (res.codigoError === "VAL-PERFIL") {
              this.sinPermiso = true;
              this.paraLatido();
              this.sinPermisoSinDetalle();
            }
            this.error =
              res.codigoError === "AUTH-001"
                ? "Tu sesión caducó. Vuelve a entrar para seguir trabajando."
                : res.mensajeError || "No se pudo cargar la bandeja.";
            this.solicitudes = [];
            // Sin esto el tablero se pinta de `columnas`, que seguirian
            // con las tarjetas viejas bajo el aviso de error.
            this.reparte();
            // La posición que esperaba a esta carga ya no aplica: una carga
            // posterior la repondría a destiempo.
            if (!this.abierta) {
              this.posicion = null;
            }
            this.cdr.detectChanges();
            return;
          }
          this.solicitudes = res.solicitudes || [];
          this.totalItems = res.totalItems || 0;
          this.responsables = res.responsables || [];
          this.sinConexion = false;
          this.sinPermiso = false;
          this.sesionCaida = false;
          // Una carga pedida a mano no avisa de "nuevas": lo que llegue
          // es, por definicion, lo que el usuario acaba de pedir ver.
          this.recuerda(this.solicitudes);
          this.reparte();
          this.cdr.detectChanges();
          // Se regresó del detalle mientras esta carga estaba en camino: el
          // tablero acaba de volver a existir y aquí sí se puede reponer.
          if (!this.abierta && this.posicion) {
            this.reponPosicion();
          }
        },
        error: () => {
          this.cargando = false;
          if (mio !== this.sello) {
            return;
          }
          this.error = "No se pudo conectar con el servidor.";
          this.solicitudes = [];
          this.reparte();
          if (!this.abierta) {
            this.posicion = null;
          }
          this.cdr.detectChanges();
        },
      });
  }

  /**
   * Reparte lo que llego en las cinco columnas.
   *
   * Va a un campo y NO a un getter: un getter que devuelve arreglos
   * nuevos en cada ciclo de deteccion hace que el tablero se repinte
   * solo sin parar.
   */
  private reparte(): void {
    // Se conserva la página en la que iba cada columna: recargar tras
    // resolver una no debería mandar al evaluador de vuelta al principio.
    const antes: { [t: string]: number } = {};
    this.columnas.forEach((c) => (antes[c.titulo] = c.pagina));

    this.columnas = COLUMNAS_SIA.map((c) => {
      const suyas = this.solicitudes.filter(
        (s) => s.estatus && c.estatus.indexOf(s.estatus) >= 0,
      );
      const paginas = Math.max(1, Math.ceil(suyas.length / this.POR_COLUMNA));
      return {
        titulo: c.titulo,
        icono: c.icono,
        color: c.color,
        pista: c.pista,
        vacia: c.vacia,
        estatus: c.estatus,
        solicitudes: suyas,
        // Si la columna encogió, la página vuelve al rango válido: si no,
        // se quedaría en blanco.
        pagina: Math.min(antes[c.titulo] || 1, paginas),
      };
    });
  }

  /* ---------------- Paginado por columna ----------------
     Cinco por columna. Con más, el tablero se vuelve una lista larga y
     deja de leerse de un vistazo, que es para lo que sirve. */

  readonly POR_COLUMNA = 5;

  /** Las cinco que toca ver de esa columna. */
  pagina(c: Columna): SolicitudIA[] {
    const desde = (c.pagina - 1) * this.POR_COLUMNA;
    return c.solicitudes.slice(desde, desde + this.POR_COLUMNA);
  }

  paginas(c: Columna): number {
    return Math.max(1, Math.ceil(c.solicitudes.length / this.POR_COLUMNA));
  }

  /** Se llama pasaPagina y no mueve: mueve() ya existe, y mueve ESTATUS. */
  pasaPagina(c: Columna, delta: number): void {
    const destino = c.pagina + delta;
    if (destino < 1 || destino > this.paginas(c)) {
      return;
    }
    c.pagina = destino;
    this.cdr.detectChanges();
  }

  /** "6–10 de 23", para saber dónde va uno. */
  rango(c: Columna): string {
    if (!c.solicitudes.length) {
      return "";
    }
    const desde = (c.pagina - 1) * this.POR_COLUMNA + 1;
    const hasta = Math.min(c.pagina * this.POR_COLUMNA, c.solicitudes.length);
    return desde + "–" + hasta + " de " + c.solicitudes.length;
  }

  filtra(): void {
    this.cargar();
  }

  limpiaFiltros(): void {
    this.busca = "";
    this.filtroEstatus = "";
    this.filtroResponsable = null;
    this.filtra();
  }

  get hayFiltro(): boolean {
    return !!(this.busca.trim() || this.filtroEstatus || this.filtroResponsable);
  }

  trackColumna = (_: number, c: Columna) => c.titulo;

  /* ================= El detalle ================= */

  /*
   * El detalle es una pantalla, no un modal (23/09/2026), y la dirección
   * dice cuál está abierta: /admin/solicitudes-ia/gestion?solicitud=42.
   * Abrir y regresar cambian la dirección, y lo que se pinta lo decide
   * siguelaUrl(). Así el Atrás del navegador hace lo mismo que Regresar,
   * recargar deja la misma solicitud abierta y el enlace se puede pasar.
   */

  /**
   * Abrir desde una tarjeta o desde un aviso.
   *
   * Se pinta YA (muestra) y además se escribe en la dirección; cuando la
   * dirección avisa del cambio, siguelaUrl ve que ya es la abierta y no
   * hace nada.
   */
  abre(s: SolicitudIA): void {
    // El clic con que se suelta un arrastre no abre la tarjeta. Y si el
    // servidor ya dijo que esta pantalla no es para quien entró, tampoco
    // (quedaban tarjetas viejas que se podían picar).
    if (s.idSolicitudIa == null || this.arrastrando || this.sinPermiso) {
      return;
    }
    const yaHabiaUna = !!this.abierta;
    if (!yaHabiaUna) {
      // Desde el tablero: detrás queda la entrada del tablero en el
      // historial, así que Regresar es "Atrás" (ver regresar()).
      this.guardaPosicion(s.idSolicitudIa);
      this.desdeTablero = true;
    }
    this.muestra(s);
    // Si ya había una abierta (se llegó aquí desde un aviso), la entrada se
    // REEMPLAZA: Regresar tiene que llevar al tablero, no a la anterior.
    // giaTablero queda en la entrada del historial (history.state): con
    // Adelante, recargar o al volver de otra pantalla se sabe que detrás
    // está el tablero (ver siguelaUrl).
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { solicitud: s.idSolicitudIa },
      queryParamsHandling: "merge",
      replaceUrl: yaHabiaUna,
      state: { giaTablero: this.desdeTablero },
    });
  }

  /** Regresar al tablero: el botón, Escape y el Atrás del navegador. */
  regresar(): void {
    if (!this.abierta) {
      return;
    }
    if (this.desdeTablero) {
      // La entrada de justo antes es el tablero: volver a ella deja el
      // historial como estaba, y Adelante vuelve a abrir la solicitud.
      this.location.back();
      return;
    }
    // Se llegó por la dirección (recargar, un enlace): no hay tablero
    // detrás. Se quita la solicitud de la dirección en su lugar.
    this.quitaDeLaUrl();
  }

  /** Lo que dice la dirección manda: abrir, cambiar o cerrar el detalle. */
  private siguelaUrl(valor: string | null): void {
    const id = valor != null && /^\d+$/.test(valor) ? Number(valor) : null;
    if (id == null) {
      if (this.abierta) {
        this.cierra();
      }
      // Aunque ya estuviera cerrada: cuando se cierra por una falla o por
      // falta de permiso (saleDelDetalle), el tablero se repone AQUÍ, al
      // llegar la navegación que quitó la solicitud, y no antes: así su
      // foco y su scroll van después del scrollTo(0,0) del layout.
      this.reponPosicion();
      return;
    }
    if (this.abierta && this.abierta.idSolicitudIa === id) {
      return;
    }
    // Ya se sabe que esta pantalla no es para quien entró (el latido se
    // apagó con VAL-PERFIL): un enlace pegado después no abre el detalle.
    if (this.sinPermiso) {
      if (this.abierta) {
        this.cierra();
      }
      // Igual que saleDelDetalle: si detrás está el tablero, Atrás;
      // reemplazar dejaba dos entradas del tablero seguidas.
      const previo = this.location.getState() as { giaTablero?: boolean } | null;
      if (previo && previo.giaTablero) {
        this.location.back();
      } else {
        this.quitaDeLaUrl();
      }
      return;
    }
    // Adelante, recargar, volver de otra pantalla o un enlace: lo que haya
    // detrás en el historial lo dice la propia entrada (abre() le deja
    // giaTablero). Un enlace pegado no lo trae: ahí Regresar quita la
    // solicitud de la dirección en vez de ir "Atrás".
    const estado = this.location.getState() as { giaTablero?: boolean } | null;
    this.desdeTablero = !!(estado && estado.giaTablero);
    if (!this.abierta) {
      // El tablero todavía se ve: se guarda dónde está para dejarlo igual.
      this.guardaPosicion(id);
    }
    const conocida = this.solicitudes.find((x) => x.idSolicitudIa === id);
    this.muestra(conocida || ({ idSolicitudIa: id } as SolicitudIA));
  }

  /**
   * El servidor dice que esta pantalla no es para quien entró (no es de
   * los cinco, o le quitaron el perfil a media sesión): tampoco se queda en
   * un detalle abierto. Con un enlace ?solicitud= el dueño de la solicitud
   * veía la pantalla del evaluador.
   */
  private sinPermisoSinDetalle(): void {
    if (this.abierta) {
      this.saleDelDetalle();
    }
  }

  /**
   * Cerrar el detalle sin que lo pidiera el usuario (no se pudo abrir, o ya
   * no tiene permiso) y quitar la solicitud de la dirección. Igual que
   * regresar(): si detrás está el tablero se vuelve a él con Atrás; si no,
   * se reemplaza. Reemplazar teniendo el tablero detrás dejaba dos entradas
   * del tablero seguidas, y el primer Atrás no hacía nada. El tablero lo
   * repone siguelaUrl cuando la dirección avisa.
   */
  private saleDelDetalle(): void {
    const volver = this.desdeTablero;
    this.cierra();
    if (volver) {
      this.location.back();
    } else {
      this.quitaDeLaUrl();
    }
  }

  private quitaDeLaUrl(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { solicitud: null },
      queryParamsHandling: "merge",
      replaceUrl: true,
    });
  }

  /** Pinta la solicitud y pide al servidor su detalle, sus archivos y su DAR. */
  private muestra(s: SolicitudIA): void {
    if (s.idSolicitudIa == null) {
      return;
    }
    const id = s.idSolicitudIa;
    const eraOtra = this.abierta && this.abierta.idSolicitudIa !== id;
    if (eraOtra) {
      this.guardaBorrador();
    }
    this.cargandoDetalle = true;
    this.abierta = s;
    this.historial = [];
    this.archivos = [];
    this.evidenciasEval = [];
    this.evidenciaCarga = "listo";
    this.adjuntos = (this.adjuntosPorSol.get(id) || []).slice();
    this.comentario = this.borradores.get(id) || "";
    // Lo que colgaba de la anterior (desde un aviso se pasa de una a otra
    // sin regresar al tablero).
    if (eraOtra) {
      this.darAbierto = false;
      this.dar = null;
    }
    this.cdr.detectChanges();
    // En pantallas chicas la página rueda: si el tablero estaba bajado, el
    // detalle aparecería a medias. Se sube sólo si hace falta.
    const host = this.host.nativeElement;
    if (host.getBoundingClientRect().top < 0) {
      host.scrollIntoView({ block: "start" });
    }
    // El foco al título: quien usa lector de pantalla oye qué se abrió, y
    // quien usa teclado arranca desde arriba y no desde la tarjeta, que ya
    // no se ve.
    if (this.detalleTitulo) {
      this.detalleTitulo.focus({ preventScroll: true });
    }

    this.service.detalle(id).subscribe({
      next: (res) => {
        // Se pasó a otra antes de que llegara esta (Atrás/Adelante rápido,
        // un aviso): esta respuesta ya no es de la que se ve.
        if (!this.esLaAbierta(id)) {
          return;
        }
        this.cargandoDetalle = false;
        if (res.codigoError) {
          this.cierraPorFalla();
          if (!this.seCayoLaSesion(res)) {
            this.avisa(res.mensajeError || "No se pudo abrir la solicitud.", "error");
          }
          this.cdr.detectChanges();
          return;
        }
        this.abierta = res.solicitud || s;
        this.historial = res.historial || [];
        this.cdr.detectChanges();
        this.alFinalDeLaHistoria();
        this.cargaArchivos(id);
        this.cargaDar(id);
        this.cargaEvidenciaEval(id);
      },
      error: () => {
        if (!this.esLaAbierta(id)) {
          return;
        }
        this.cargandoDetalle = false;
        this.cierraPorFalla();
        this.avisa("No se pudo conectar con el servidor.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  private esLaAbierta(id: number): boolean {
    return !!this.abierta && this.abierta.idSolicitudIa === id;
  }

  /**
   * No se pudo abrir: de vuelta al tablero, y la solicitud fuera de la
   * dirección. Si se quedara, recargar volvería a intentar abrirla.
   */
  private cierraPorFalla(): void {
    this.saleDelDetalle();
  }

  /** Guarda lo escrito y no enviado de la solicitud abierta. */
  private guardaBorrador(): void {
    const id = this.abierta ? this.abierta.idSolicitudIa : null;
    if (id == null) {
      return;
    }
    if (this.comentario.trim()) {
      this.borradores.set(id, this.comentario);
    } else {
      this.borradores.delete(id);
    }
    if (this.adjuntos.length) {
      this.adjuntosPorSol.set(id, this.adjuntos.slice());
    } else {
      this.adjuntosPorSol.delete(id);
    }
  }

  /**
   * Dónde estaba el tablero al abrir una solicitud. Mientras se ve el
   * detalle el tablero está en display:none, y al volver a verse el
   * navegador lo pinta desde arriba: sin esto se regresaba a otra altura
   * de la columna y a otra posición del tablero a lo ancho.
   */
  private guardaPosicion(idSolicitud: number): void {
    const t = this.tablero;
    this.posicion = {
      idSolicitud,
      tableroX: t ? t.scrollLeft : 0,
      columnas: t
        ? Array.from(t.querySelectorAll<HTMLElement>(".gia-col__cuerpo")).map((c) => c.scrollTop)
        : [],
    };
  }

  /**
   * Deja el tablero donde estaba y el foco en la tarjeta de la que se
   * salió. Va después de cierra(), que ya repintó el tablero visible: antes
   * no mide nada y el scroll no se puede poner.
   */
  private reponPosicion(): void {
    const p = this.posicion;
    const t = this.tablero;
    if (!p || !t) {
      // Sin tablero en este momento (se está recargando la bandeja después
      // de una acción): la posición se queda guardada y la repone cargar()
      // cuando lo vuelva a pintar.
      return;
    }
    this.posicion = null;
    t.scrollLeft = p.tableroX;
    t.querySelectorAll<HTMLElement>(".gia-col__cuerpo").forEach((c, i) => {
      if (p.columnas[i] != null) {
        c.scrollTop = p.columnas[i];
      }
    });
    // El foco y el "que se vea", un momento después: el layout del admin
    // sube la página a 0 en cada navegación (admin-layout, NavigationEnd),
    // y quitar ?solicitud de la dirección es una navegación. En el teléfono,
    // donde rueda la página y no las columnas, la tarjeta quedaba abajo,
    // fuera de vista y con el foco puesto. 'nearest' no mueve nada si ya se
    // ve (y trae la columna a la vista si la tarjeta cambió de columna).
    setTimeout(() => {
      if (this.abierta || this.destruido) {
        return;
      }
      const tarjeta = t.querySelector<HTMLElement>(`[data-sol="${p.idSolicitud}"]`);
      if (!tarjeta || !tarjeta.isConnected) {
        return;
      }
      tarjeta.scrollIntoView({ block: "nearest", inline: "nearest" });
      // El foco, sólo si nadie lo tiene. Con un aviso de SweetAlert abierto
      // (no se pudo abrir, sesión caducada) se ESPERA a que se cierre: si
      // se ponía ya, el Enter para cerrarlo caía en la tarjeta y la volvía a
      // abrir; si se descartaba, el foco se quedaba en <body>. Y si el
      // usuario ya está en el buscador, no se le saca de ahí.
      const enfoca = (intentos: number): void => {
        if (this.abierta || this.destruido || !tarjeta.isConnected) {
          return;
        }
        if (Swal.isVisible()) {
          if (intentos > 0) {
            setTimeout(() => enfoca(intentos - 1), 150);
          }
          return;
        }
        const activo = document.activeElement;
        if (!activo || activo === document.body) {
          tarjeta.focus({ preventScroll: true });
        }
      };
      // Hasta un minuto esperando al aviso (400 × 150 ms); después, nada.
      enfoca(400);
    });
  }

  /**
   * Escape regresa, de arriba abajo: con el DAR encima, cierra ése y deja
   * el detalle donde estaba.
   *
   * No hace nada mientras se escribe (Escape en la caja de comentar
   * sacaba de la solicitud y se perdía lo escrito) ni con una ventanita de
   * SweetAlert abierta: ese Escape es de ella.
   */
  @HostListener("document:keydown.escape", ["$event"])
  alEscape(ev?: Event): void {
    if (Swal.isVisible()) {
      return;
    }
    if (this.darAbierto) {
      this.cierraDar();
      return;
    }
    const donde = ev && (ev.target as HTMLElement | null);
    if (donde && donde.closest && donde.closest("textarea, input, select, [contenteditable='true']")) {
      return;
    }
    if (this.abierta) {
      this.regresar();
    }
  }

  cierra(): void {
    this.guardaBorrador();
    // Se cierra TODO lo que colgaba del detalle. Si no, al abrir la
    // siguiente solicitud reapareceria el DAR de la anterior.
    this.darAbierto = false;
    this.comentario = "";
    this.abierta = null;
    this.historial = [];
    this.archivos = [];
    this.evidenciasEval = [];
    this.evidenciaCarga = "listo";
    this.adjuntos = [];
    this.dar = null;
    this.darCarga = "listo";
    this.archivosCarga = "listo";
    this.cdr.detectChanges();
  }

  /** El DAR capturado en pantalla, si lo hay. */
  private cargaDar(idSolicitud: number): void {
    this.dar = null;
    this.darCarga = "cargando";
    this.cdr.detectChanges();
    this.service.dar(idSolicitud).subscribe({
      next: (res) => {
        // De otra solicitud que ya no se ve (se regresó o se pasó a otra).
        if (!this.esLaAbierta(idSolicitud)) {
          return;
        }
        // codigoError NO es "no hay DAR": es "no se pudo consultar".
        this.darCarga = res.codigoError ? "falla" : "listo";
        this.dar = res.codigoError ? null : res.dar || null;
        this.cdr.detectChanges();
      },
      error: () => {
        if (!this.esLaAbierta(idSolicitud)) {
          return;
        }
        this.dar = null;
        this.darCarga = "falla";
        this.cdr.detectChanges();
      },
    });
  }

  /**
   * Que ensenar en el apartado del DAR: lo que hay, o por que no hay.
   *
   * "hay" gana a todo: si llego el DAR capturado, que falle la consulta
   * de los adjuntos no debe esconderlo.
   */
  get estadoDar(): "hay" | "cargando" | "falla" | "vacio" {
    if (this.dar || this.archivos.length) {
      return "hay";
    }
    if (this.darCarga === "cargando" || this.archivosCarga === "cargando") {
      return "cargando";
    }
    if (this.darCarga === "falla" || this.archivosCarga === "falla") {
      return "falla";
    }
    return "vacio";
  }

  /**
   * Que alguna de las dos consultas haya fallado, HAYA O NO algo que
   * enseñar.
   *
   * estadoDar contesta "qué pinto"; esto contesta "¿me falta algo?", y
   * son preguntas distintas. Antes eran la misma, y con eso bastaba UN
   * adjunto cualquiera —un PDF de apoyo— para que estadoDar dijera "hay"
   * y el aviso de "no se pudo consultar el DAR" desapareciera de la
   * pantalla: el evaluador veía un archivo y dictaminaba creyendo que
   * eso era todo lo que había.
   */
  get falloAlgoDelDar(): boolean {
    return this.darCarga === "falla" || this.archivosCarga === "falla";
  }

  /** Volver a preguntar por el DAR y por los adjuntos. */
  reintentaDar(): void {
    if (!this.abierta || this.abierta.idSolicitudIa == null) {
      return;
    }
    this.cargaDar(this.abierta.idSolicitudIa);
    this.cargaArchivos(this.abierta.idSolicitudIa);
  }

  abreDar(): void {
    this.darAbierto = true;
    this.cdr.detectChanges();
  }

  cierraDar(): void {
    this.darAbierto = false;
    this.cdr.detectChanges();
  }

  private cargaArchivos(idSolicitud: number): void {
    this.archivos = [];
    this.archivosCarga = "cargando";
    this.cdr.detectChanges();
    this.evidencias.consultarEvidencias(ORIGEN_SOLICITUD_IA, idSolicitud).subscribe({
      next: (res: any) => {
        // De otra solicitud que ya no se ve (se regresó o se pasó a otra).
        if (!this.esLaAbierta(idSolicitud)) {
          return;
        }
        this.archivosCarga = res && res.codigoError ? "falla" : "listo";
        this.archivos = (res && !res.codigoError && res.evidencias) || [];
        this.cdr.detectChanges();
      },
      error: () => {
        if (!this.esLaAbierta(idSolicitud)) {
          return;
        }
        this.archivos = [];
        this.archivosCarga = "falla";
        this.cdr.detectChanges();
      },
    });
  }

  /* ================= Quién soy ================= */

  /**
   * El id de quien está dentro, del CosData que deja el login. Sirve para
   * saber en pantalla si la solicitud abierta la atiendo yo; el servidor
   * vuelve a comprobarlo en cada movimiento, así que un CosData viejo sólo
   * puede esconder botones, nunca dejar mover lo ajeno.
   */
  private miId: number | null = null;

  private leeQuienSoy(): void {
    try {
      const raw = localStorage.getItem("CosData");
      const sesion = raw ? JSON.parse(raw) : null;
      // El CosData que deja el login trae usuario.id siempre; idUsuarioSession
      // sólo desde el WAR del 22/09 (antes LoginService.getSession no lo
      // copiaba). Con sólo el segundo, en producción "la atendía otra
      // persona" siendo uno mismo. Se leen los dos, como las incidencias.
      const id = sesion ? (sesion.idUsuarioSession ?? sesion.usuario?.id) : null;
      this.miId = id != null && !Number.isNaN(Number(id)) ? Number(id) : null;
    } catch {
      this.miId = null;
    }
  }

  /** La atiendo yo: es la única que puedo mover de etapa. */
  esMia(s: SolicitudIA | null): boolean {
    return (
      !!s &&
      s.idUsuarioResponsable != null &&
      this.miId != null &&
      Number(s.idUsuarioResponsable) === this.miId
    );
  }

  /** La atiende otro de los cinco: se ve, no se mueve. */
  laAtiendeOtro(s: SolicitudIA | null): boolean {
    return !!s && s.idUsuarioResponsable != null && !this.esMia(s);
  }

  /* ================= Arrastrar en el tablero =================
     Mover una tarjeta de columna es lo mismo que pulsar el botón de esa
     etapa en el detalle: abre el mismo diálogo (comentario obligatorio;
     área del catálogo al autorizar) y llama al mismo endpoint. La tarjeta
     NO cambia de columna al soltar: cambia cuando el servidor contesta y
     el tablero se recarga. Así lo que se ve es siempre lo que hay. */

  /** La pedí yo. Ni la atiendo ni la dictamino, aunque sea de los cinco. */
  esMiSolicitud(s: SolicitudIA | null): boolean {
    return (
      !!s &&
      s.idUsuarioSolicita != null &&
      this.miId != null &&
      Number(s.idUsuarioSolicita) === this.miId
    );
  }

  /** Se puede arrastrar si tiene a dónde ir: ver destinosDe(). */
  puedeArrastrar(s: SolicitudIA): boolean {
    return (
      this.abiertaAun(s) &&
      (!s.idUsuarioResponsable || this.esMia(s)) &&
      this.destinosDe(s).length > 0
    );
  }

  /**
   * A qué estatus puede ir una tarjeta. Espejo de exigeTransicion() del
   * servidor, que es quien manda: esto sólo evita ofrecer un hueco donde
   * el servidor diría que no.
   */
  private destinosDe(s: SolicitudIA): EstatusSolicitudIA[] {
    if (!s.idUsuarioResponsable) {
      // Libre: lo único que se puede hacer es tomarla, y tomarla sólo la
      // pasa a Proceso desde SOLICITADA (asignar() en el servidor). Una
      // libre en otro estatus —dato viejo— se queda para el botón del
      // detalle: arrastrarla a Proceso la asignaría sin moverla, y el
      // tablero diría «ya está en proceso» con la tarjeta donde estaba.
      // Y nadie atiende lo suyo, ni siendo de los cinco.
      return s.estatus === "SOLICITADA" && !this.esMiSolicitud(s) ? ["EN_EVALUACION"] : [];
    }
    // Nadie dictamina lo suyo: si la pedí yo, no ofrezco autorizar ni
    // rechazar (el servidor contesta VAL-004). Puede pasar con datos
    // viejos, de antes de que asignar() bloqueara tomar la propia.
    const mia = this.esMiSolicitud(s);
    const sinDictamen = (xs: EstatusSolicitudIA[]) =>
      mia ? xs.filter((x) => x !== "APROBADA" && x !== "RECHAZADA") : xs;
    switch (s.estatus) {
      case "SOLICITADA":
        return sinDictamen(["EN_EVALUACION", "INFORMACION_REQUERIDA", "RECHAZADA"]);
      case "EN_EVALUACION":
        return sinDictamen(["APROBADA", "RECHAZADA", "INFORMACION_REQUERIDA"]);
      case "INFORMACION_REQUERIDA":
        return sinDictamen(["EN_EVALUACION", "RECHAZADA"]);
      default:
        return [];
    }
  }

  /** La columna sólo acepta la tarjeta si el movimiento es válido. Es una flecha: el CDK la llama sin `this`. */
  puedeRecibir = (drag: CdkDrag<SolicitudIA>, lista: CdkDropList<Columna>): boolean => {
    const s = drag.data;
    const c = lista.data;
    if (!s || !c || !s.estatus || c.estatus.includes(s.estatus)) {
      return false;
    }
    return this.destinosDe(s).includes(c.estatus[0]);
  };

  soltar(ev: CdkDragDrop<Columna, Columna, SolicitudIA>): void {
    const s = ev.item.data;
    const c = ev.container.data;
    // isPointerOverContainer: el CDK conserva como contenedor la última
    // columna que aceptó la tarjeta aunque el puntero ya no esté encima.
    // Sin esto, pasar por Cerradas y soltar fuera del tablero abría «No
    // autorizar la IA», que es lo contrario de lo que quiere quien suelta
    // en el vacío para cancelar.
    if (!s || !c || !ev.isPointerOverContainer || ev.previousContainer === ev.container) {
      return;
    }
    const destino = c.estatus[0];
    if (!this.destinosDe(s).includes(destino)) {
      return;
    }
    this.ejecutaDestino(s, destino);
  }

  /**
   * Llevar la solicitud a una etapa: lo mismo arrastrando la tarjeta que
   * picando la etapa en el detalle. Una libre sólo se toma (y tomarla la
   * pasa a Proceso); las demás, el diálogo de esa etapa.
   */
  private ejecutaDestino(s: SolicitudIA, destino: EstatusSolicitudIA): void {
    if (!s.idUsuarioResponsable) {
      this.tomar(s);
      return;
    }
    switch (destino) {
      case "EN_EVALUACION":
        this.evaluar(s);
        break;
      case "INFORMACION_REQUERIDA":
        this.pedirInfo(s);
        break;
      case "RECHAZADA":
        this.rechazar(s);
        break;
      case "APROBADA":
        this.aprobar(s);
        break;
    }
  }

  /* ---------- Las etapas, en la cabecera del detalle ---------- */

  /** Si la solicitud abierta está en esta etapa (columna del tablero). */
  esEtapaActual(c: Columna): boolean {
    const s = this.abierta;
    return !!s && !!s.estatus && c.estatus.includes(s.estatus);
  }

  /**
   * Si se puede llevar la solicitud abierta a esta etapa. Es la misma
   * pregunta que hace el tablero al arrastrar (puedeArrastrar +
   * puedeRecibir): lo que ofrece la cabecera es lo que el servidor acepta.
   */
  puedeIrA(c: Columna): boolean {
    const s = this.abierta;
    if (!s || !s.estatus || this.cargandoDetalle || !this.puedeArrastrar(s)) {
      return false;
    }
    return !c.estatus.includes(s.estatus) && this.destinosDe(s).includes(c.estatus[0]);
  }

  /** Si hay alguna etapa a la que se pueda llevar (para la nota). */
  get puedeMoverla(): boolean {
    return this.columnas.some((c) => this.puedeIrA(c));
  }

  /** Picar una etapa: el diálogo de esa etapa, o tomarla si está libre. */
  moverA(c: Columna): void {
    const s = this.abierta;
    if (!s || this.trabajando || !this.puedeIrA(c)) {
      return;
    }
    this.ejecutaDestino(s, c.estatus[0]);
  }

  /** El globo de cada etapa: dónde está, qué hace picarla, o qué es. */
  queHaceEtapa(c: Columna): string {
    const s = this.abierta;
    if (!s) {
      return c.titulo;
    }
    if (this.esEtapaActual(c)) {
      return `Está en ${c.titulo}: ${c.pista.toLowerCase()}.`;
    }
    if (this.puedeIrA(c)) {
      if (!s.idUsuarioResponsable) {
        return "Tomarla: pasa a Proceso y la atiendes tú.";
      }
      switch (c.estatus[0]) {
        case "EN_EVALUACION":
          return "Volver a Proceso.";
        case "INFORMACION_REQUERIDA":
          return "Pedir información a quien la pidió.";
        case "APROBADA":
          return "Autorizar la IA.";
        case "RECHAZADA":
          return "No autorizar la IA.";
      }
    }
    return `${c.titulo}: ${c.pista.toLowerCase()}.`;
  }

  /**
   * Debajo de las etapas, en palabras: qué se puede hacer o por qué no.
   * Antes esto vivía en el pie; ahora el pie sólo sale cuando hay botones.
   */
  get notaEtapas(): string {
    const s = this.abierta;
    if (!s) {
      return "";
    }
    if (!this.abiertaAun(s)) {
      return "Ya está cerrada: no cambia de etapa.";
    }
    if (!s.idUsuarioResponsable) {
      // Nadie atiende lo suyo: el servidor contesta VAL-004 al tomarla.
      if (this.esMiSolicitud(s)) {
        return "La pediste tú: la tiene que atender otro de los cinco.";
      }
      // Sólo una libre en Abiertas se toma llevándola a Proceso. Una libre
      // en otra etapa (dato viejo) se toma con el botón de abajo.
      return this.puedeMoverla
        ? "Pica «Proceso» para tomarla: sólo quien la toma la mueve de etapa."
        : "Tómala con «Tomarla», abajo: sólo quien la toma la mueve de etapa.";
    }
    if (!this.esMia(s)) {
      return `La atiende ${s.responsable || "otra persona"}: sólo esa persona la mueve de etapa.`;
    }
    return this.esMiSolicitud(s)
      ? "La atiendes tú, pero la pediste tú: el dictamen lo da otro de los cinco."
      : "La atiendes tú: pica una etapa para moverla.";
  }

  /** Si el pie lleva botones: tomarla (libre y no mía) o atenderla (mía). */
  hayAcciones(s: SolicitudIA | null): boolean {
    return !!s && ((!s.idUsuarioResponsable && !this.esMiSolicitud(s)) || this.esMia(s));
  }

  /**
   * Mientras vuela una tarjeta, y un instante después: el clic con que se
   * suelta no la abre, y el latido no reparte las columnas debajo.
   *
   * Con red de seguridad: si la tarjeta desapareciera en el aire (otro la
   * movió y el tablero se repintó), el CDK no llegaría a avisar que
   * terminó y esto se quedaría en verdadero para siempre, dejando el
   * tablero sin abrir ninguna tarjeta.
   */
  private arrastrando = false;
  private relojArrastre: any = null;

  alEmpezarArrastre(): void {
    this.arrastrando = true;
    clearTimeout(this.relojArrastre);
    this.relojArrastre = setTimeout(() => (this.arrastrando = false), 30000);
    // La copia que vuela lleva pointer-events:none, así que el cursor lo
    // decide lo que queda debajo y parpadeaba entre flecha, dedo y manita.
    // La misma clase que usa el paneo deja la manita cerrada en todo el
    // tablero mientras dura el arrastre.
    this.tablero?.classList.add("arrastrando");
  }

  alSoltarArrastre(): void {
    this.tablero?.classList.remove("arrastrando");
    clearTimeout(this.relojArrastre);
    this.relojArrastre = setTimeout(() => (this.arrastrando = false), 0);
  }

  /* ================= Acciones =================
     Todas reciben la solicitud sobre la que actúan; sin argumento es la
     abierta en el detalle (los botones), con argumento la que se arrastró. */

  /** Tomarla: se asigna a quien pulsa y pasa sola a Proceso. */
  tomar(s: SolicitudIA | null = this.abierta): void {
    if (!s || s.idSolicitudIa == null) {
      return;
    }
    const id = s.idSolicitudIa;
    this.trabajando = true;
    this.service.asignar(id).subscribe({
      next: (res) => {
        this.trabajando = false;
        if (res.codigoError) {
          if (!this.seCayoLaSesion(res)) {
            this.avisa(res.mensajeError || "No se pudo tomar la solicitud.", "error");
            // VAL-003 es «otro se adelantó»: lo que el tablero enseña ya
            // no es lo que hay. Se recarga en vez de esperar al latido,
            // que tarda hasta 25 s y deja repetir el mismo error.
            if (res.codigoError === "VAL-003") {
              this.recarga();
              return;
            }
          }
          this.cdr.detectChanges();
          return;
        }
        // Su aviso sobra en cuanto es de alguien.
        this.quitaAviso(id, false);
        this.avisa("La solicitud es tuya y ya está en proceso.", "success");
        this.recarga();
      },
      error: () => {
        this.trabajando = false;
        this.avisa("No se pudo conectar con el servidor.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  /** Empezar a evaluarla, o devolverla pidiendo información. */
  mueve(destino: string, titulo: string, pista: string, s: SolicitudIA | null = this.abierta): void {
    if (!s || s.idSolicitudIa == null) {
      return;
    }
    Swal.fire({
      title: titulo,
      text: s.folio ? `${s.folio} · ${s.nombreIa || ""}` : undefined,
      input: "textarea",
      inputPlaceholder: pista,
      inputAttributes: { maxlength: "1000" },
      showCancelButton: true,
      confirmButtonText: "Confirmar",
      cancelButtonText: "Cancelar",
      // Todo movimiento lleva comentario: un cambio de estatus sin una
      // línea que lo explique no le sirve a nadie tres meses después.
      inputValidator: (v) => (v && v.trim() ? null : "Escribe por qué."),
    }).then((r) => {
      if (r.isConfirmed) {
        this.aplica(destino, r.value, null, s);
      }
    });
  }

  evaluar(s: SolicitudIA | null = this.abierta): void {
    this.mueve("EN_EVALUACION", "Poner en evaluación",
      "Qué vas a revisar, o cualquier nota para el expediente.", s);
  }

  pedirInfo(s: SolicitudIA | null = this.abierta): void {
    this.mueve("INFORMACION_REQUERIDA", "Pedir más información",
      "Di exactamente qué falta: el solicitante va a leer esto.", s);
  }

  /**
   * Evidencia que todavía no está en el servidor: archivos del clip sin
   * enviar, o una subida en curso. Dictaminar con eso pendiente la perdía
   * para siempre: después del dictamen el servidor ya no acepta evidencia.
   */
  private evidenciaPendiente(s: SolicitudIA | null): number {
    if (!s || s.idSolicitudIa == null) {
      return 0;
    }
    const id = s.idSolicitudIa;
    const enCaja = this.esLaAbierta(id) ? this.adjuntos.length : (this.adjuntosPorSol.get(id) || []).length;
    return enCaja + (this.subiendoEn.has(id) ? 1 : 0);
  }

  /** Si hay evidencia pendiente, lo dice y devuelve true: no se dictamina. */
  private frenaPorEvidencia(s: SolicitudIA | null): boolean {
    if (!s || !this.evidenciaPendiente(s)) {
      return false;
    }
    const id = s.idSolicitudIa!;
    this.avisa(
      this.subiendoEn.has(id)
        ? "Todavía se está subiendo la evidencia. Espera a que termine y luego dictamina: después del dictamen ya no se puede agregar."
        : "Tienes archivos de evidencia sin enviar en la caja de comentar. Envíalos o quítalos antes de dictaminar: después del dictamen ya no se puede agregar evidencia.",
      "warning",
    );
    return true;
  }

  rechazar(s: SolicitudIA | null = this.abierta): void {
    if (this.frenaPorEvidencia(s)) {
      return;
    }
    this.mueve("RECHAZADA", "No autorizar la IA",
      "El motivo. El solicitante va a leerlo.", s);
  }

  /**
   * Aprobar. Pide además el área del CATÁLOGO ISO, que es con la que el
   * activo entra al inventario — y NO es la del solicitante: son catálogos
   * distintos y sus ids no coinciden.
   */
  aprobar(s: SolicitudIA | null = this.abierta): void {
    if (!s || s.idSolicitudIa == null || this.frenaPorEvidencia(s)) {
      return;
    }
    // Sin catalogo, el dialogo salia con el select vacio y su propio
    // validador impidiendo confirmar: un callejon sin salida.
    if (!this.catAreas.length) {
      Swal.fire({
        title: "Falta el catálogo de áreas ISO",
        text:
          "No se pudo cargar el catálogo de áreas del ISO, y sin área la IA " +
          "no puede entrar al inventario. Vuelve a intentarlo.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Reintentar",
        cancelButtonText: "Cerrar",
      }).then((r) => {
        if (r.isConfirmed) {
          this.cargaCatAreas();
        }
      });
      return;
    }

    // El nombre del área viene de la base y acaba dentro de HTML: se
    // escapa. Es texto que teclea gente, no una constante del código.
    const opciones = this.catAreas
      .map(
        (a) =>
          `<option value="${this.escapa(String(a.id))}">` +
          `${this.escapa(a.area || "")}</option>`,
      )
      .join("");
    Swal.fire({
      title: "Autorizar la IA",
      html:
        '<p style="font-size:13px;color:#64748b;margin:0 0 10px">' +
        (s.folio ? `<b>${this.escapa(s.folio)}</b> · ${this.escapa(s.nombreIa || "")}<br>` : "") +
        "Al aprobarla se da de alta sola en el inventario de IA. " +
        "El área es la del catálogo ISO, no la del solicitante." +
        "</p>" +
        '<select id="siaCatArea" class="swal2-select" style="width:90%">' +
        '<option value="">— Área del catálogo ISO —</option>' +
        opciones +
        "</select>" +
        '<textarea id="siaMotivo" class="swal2-textarea" style="width:90%" ' +
        'maxlength="1000" placeholder="Con qué criterio se autoriza."></textarea>',
      showCancelButton: true,
      confirmButtonText: "Autorizar",
      cancelButtonText: "Cancelar",
      preConfirm: () => {
        const sel = document.getElementById("siaCatArea") as HTMLSelectElement;
        const txt = document.getElementById("siaMotivo") as HTMLTextAreaElement;
        if (!sel || !sel.value) {
          Swal.showValidationMessage("Escoge el área del catálogo ISO.");
          return false;
        }
        if (!txt || !txt.value.trim()) {
          Swal.showValidationMessage("Escribe con qué criterio se autoriza.");
          return false;
        }
        return { idCatArea: Number(sel.value), motivo: txt.value.trim() };
      },
    }).then((r) => {
      if (r.isConfirmed && r.value) {
        this.aplica("APROBADA", r.value.motivo, r.value.idCatArea, s);
      }
    });
  }

  /**
   * Si el servidor dijo que no hay sesión, se avisa y se ofrece volver a
   * entrar. Devuelve true cuando ya se hizo cargo del error, para que
   * quien llama no pinte además su propio mensaje.
   *
   * No se redirige solo: quien esté a media captura merece leer qué pasó
   * antes de que la pantalla se le vaya de las manos.
   */
  private seCayoLaSesion(res: any): boolean {
    if (!res || res.codigoError !== "AUTH-001") {
      return false;
    }
    this.sesionCaida = true;
    this.paraLatido();
    this.cdr.detectChanges();
    Swal.fire({
      title: "Tu sesión caducó",
      text: "Vuelve a entrar para seguir trabajando. Lo que ya estaba guardado no se pierde.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Volver a entrar",
      cancelButtonText: "Ahora no",
    }).then((r) => {
      if (r.isConfirmed) {
        this.router.navigate(["/security/login"]);
      }
    });
    return true;
  }

  /** Lo mínimo para meter texto de la base dentro del html de un SweetAlert. */
  private escapa(t: string): string {
    return (t || "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#39;");
  }

  private aplica(
    destino: string,
    comentario: string,
    idCatArea: number | null,
    s: SolicitudIA | null = this.abierta,
  ): void {
    if (!s || s.idSolicitudIa == null) {
      return;
    }
    this.trabajando = true;
    this.service
      .mover(s.idSolicitudIa, destino, comentario, idCatArea)
      .subscribe({
        next: (res) => {
          this.trabajando = false;
          if (res.codigoError) {
            if (!this.seCayoLaSesion(res)) {
              this.avisa(res.mensajeError || "No se pudo mover la solicitud.", "error");
              // Ver la nota de tomar(): otro de los cinco se adelantó.
              if (res.codigoError === "VAL-003") {
                this.recarga();
                return;
              }
            }
            this.cdr.detectChanges();
            return;
          }
          if (destino === "APROBADA" && res.idInventarioGenerado) {
            // Entra al inventario y a la Matriz de Riesgos IA a medio llenar
            // a propósito: falta lo que sólo quien evalúa puede escribir. Se
            // ofrece ir al inventario ya. Si el servidor es anterior y no
            // manda el folio de la matriz, el aviso habla sólo del inventario.
            // Con el control de cambios de la matriz encendido no hay ACT
            // todavía: quedó propuesta y el aviso tiene que decir eso, no
            // que ya está en la matriz.
            const act = res.matrizFolioGenerado;
            const propuesta = !act && res.matrizPropuestaGenerada === "PENDIENTE";
            Swal.fire({
              title: "Autorizada",
              text: act
                ? "Ya está en el inventario de IA y en la Matriz de Riesgos IA como " + act + ". " +
                  "En el inventario le falta ciclo de vida, nivel de supervisión, ubicación e " +
                  "impactos; en la matriz, su valoración y sus amenazas. ¿Vas a completarla?"
                : propuesta
                ? "Ya está en el inventario de IA y quedó propuesta a la Matriz de Riesgos IA, " +
                  "pendiente de aprobar: su activo nace cuando se acepte en «Cambios por aprobar» de la matriz. " +
                  "En el inventario le falta ciclo de vida, nivel de supervisión, ubicación e " +
                  "impactos. ¿Vas a completarla?"
                : "Ya está en el inventario de IA, pero le falta ciclo de vida, " +
                  "nivel de supervisión, ubicación e impactos. ¿Vas a completarla?",
              icon: "success",
              showCancelButton: true,
              confirmButtonText: "Ir al inventario",
              cancelButtonText: "Luego",
            }).then((r) => {
              if (r.isConfirmed) {
                this.router.navigate(["/admin/inventario-ia/gestion"]);
              } else {
                this.recarga();
              }
            });
            return;
          }
          this.avisa("Listo.", "success");
          this.recarga();
        },
        error: () => {
          this.trabajando = false;
          this.avisa("No se pudo conectar con el servidor.", "error");
          this.cdr.detectChanges();
        },
      });
  }

  /** Ctrl+Enter (Cmd+Enter en Mac) en la caja: enviar sin ir al botón. */
  comentaConTeclado(ev: Event): void {
    ev.preventDefault();
    this.comenta();
  }

  comenta(): void {
    // trabajando / subiendoEvidencia: con Ctrl+Enter se podía enviar dos
    // veces mientras el servidor contestaba (el botón sí se deshabilita;
    // el teclado no).
    if (this.trabajando || this.subiendoEvidencia || !this.abierta || this.abierta.idSolicitudIa == null) {
      return;
    }
    // Lo que se envía y a cuál: mientras contesta el servidor se puede
    // regresar o pasar a otra, y la respuesta no debe tocar lo de otra.
    const id = this.abierta.idSolicitudIa;
    const texto = this.comentario.trim();
    const archivos = this.puedeSubirEvidencia ? this.adjuntos.slice() : [];
    if (!texto && !archivos.length) {
      return;
    }
    if (!archivos.length) {
      this.enviaComentario(id, texto, 0);
      return;
    }
    // Primero los archivos. Si alguno no entra, el comentario NO se envía
    // todavía: se queda en la caja con los que faltan, para reintentar, y
    // así el texto nunca habla de un archivo que no está.
    this.subeArchivosEvidencia(id, archivos, (fallidos, motivos) => {
      if (this.esLaAbierta(id)) {
        this.adjuntos = this.adjuntos.filter((f) => fallidos.includes(f));
        this.cargaEvidenciaEval(id);
      } else {
        // Se salió a media subida: cierra() guardó TODOS los adjuntos de
        // esa solicitud; ahí sólo deben quedar los que no entraron.
        const guardados = (this.adjuntosPorSol.get(id) || []).filter((f) => fallidos.includes(f));
        if (guardados.length) {
          this.adjuntosPorSol.set(id, guardados);
        } else {
          this.adjuntosPorSol.delete(id);
        }
      }
      if (motivos.length) {
        if (this.esLaAbierta(id)) {
          this.recargaDetalle();
        }
        this.avisa(
          "No se subieron:\n" + motivos.join("\n") +
            (texto ? "\n\nTu comentario no se envió todavía: revisa esos archivos y vuelve a enviar." : ""),
          "warning",
        );
        return;
      }
      if (texto) {
        this.enviaComentario(id, texto, archivos.length);
      } else {
        if (this.esLaAbierta(id)) {
          this.recargaDetalle();
        }
        this.avisa(archivos.length === 1 ? "Evidencia subida." : `${archivos.length} archivos de evidencia subidos.`, "success");
      }
    });
  }

  /** Guarda el comentario. conArchivos: cuántos archivos se subieron con él (para el aviso). */
  private enviaComentario(id: number, texto: string, conArchivos: number): void {
    this.trabajando = true;
    this.service.comentar(id, texto).subscribe({
      next: (res) => {
        this.trabajando = false;
        if (res.codigoError) {
          if (!this.seCayoLaSesion(res)) {
            this.avisa(res.mensajeError || "No se pudo comentar.", "error");
          }
          this.cdr.detectChanges();
          return;
        }
        // El comentario YA quedó guardado: el servidor dijo que sí. Lo
        // único que puede fallar de aquí en adelante es el refresco de la
        // historia — y si eso pasa sin decir nada, la pantalla se ve
        // igual que si no se hubiera guardado y se escribe otra vez.
        // Sólo si sigue abierta ESA y la caja dice lo que se envió: si se
        // pasó a otra, la caja ya es de la otra; y si se siguió escribiendo,
        // lo nuevo no se tira.
        if (this.esLaAbierta(id) && this.comentario.trim() === texto) {
          this.comentario = "";
        }
        // Si se salió con el comentario en la caja, cierra() lo guardó como
        // borrador; ya se envió, así que ya no es borrador.
        const guardado = this.borradores.get(id);
        if (guardado != null && guardado.trim() === texto) {
          this.borradores.delete(id);
        }
        // La caja se vacia YA, sin esperar al refresco: si no, el texto
        // sigue ahi y parece que no se mando.
        this.cdr.detectChanges();
        this.avisa(
          conArchivos
            ? `Comentario y ${conArchivos === 1 ? "1 archivo" : conArchivos + " archivos"} de evidencia guardados.`
            : "Comentario guardado.",
          "success",
        );
        this.recargaDetalle(() => {
          this.avisa(
            "Se guardó tu comentario, pero no se pudo actualizar la historia. " +
              "Vuelve a abrir la solicitud para verla completa.",
            "error",
          );
        });
      },
      error: () => {
        this.trabajando = false;
        this.avisa("No se pudo conectar con el servidor.", "error");
        this.cdr.detectChanges();
      },
    });
  }

  private recarga(): void {
    const id = this.abierta ? this.abierta.idSolicitudIa : null;
    this.cargar();
    if (id != null) {
      this.service.detalle(id).subscribe({
        next: (res) => {
          // Sólo si sigue abierta ESA: si ya se regresó al tablero, poner
          // abierta aquí volvía a abrir el detalle solo; si se pasó a otra,
          // le ponía la historia de ésta.
          if (!this.esLaAbierta(id)) {
            return;
          }
          if (!res.codigoError) {
            this.abierta = res.solicitud || this.abierta;
            this.historial = res.historial || [];
          }
          this.cdr.detectChanges();
          this.alFinalDeLaHistoria(true);
        },
        error: () => {},
      });
    }
  }

  /**
   * Vuelve a pedir la historia de la solicitud abierta.
   *
   * alFallar se llama cuando no se pudo. Quien llama decide si eso
   * importa: después de comentar importa mucho, porque la pantalla se
   * queda enseñando una historia que ya no es la de la base.
   */
  private recargaDetalle(alFallar?: () => void): void {
    if (this.abierta && this.abierta.idSolicitudIa != null) {
      const id = this.abierta.idSolicitudIa;
      this.service.detalle(id).subscribe({
        next: (res) => {
          // Ya se salió de esta solicitud: su historia no va en otra.
          if (!this.esLaAbierta(id)) {
            return;
          }
          if (res.codigoError) {
            if (alFallar) {
              alFallar();
            }
          } else {
            this.historial = res.historial || [];
          }
          this.cdr.detectChanges();
          // Lo recién comentado es lo último de la lista: que se vea.
          this.alFinalDeLaHistoria(true);
        },
        error: () => {
          if (alFallar) {
            alFallar();
          }
          this.cdr.detectChanges();
        },
      });
    }
  }

  /* ================= La evidencia de la evaluación ================= */

  /** Si quien está viendo puede subir o quitar evidencia (la misma regla del servidor). */
  get puedeSubirEvidencia(): boolean {
    const s = this.abierta;
    return (
      !!s &&
      this.esMia(s) &&
      !this.esMiSolicitud(s) &&
      (s.estatus === "EN_EVALUACION" || s.estatus === "INFORMACION_REQUERIDA")
    );
  }

  private cargaEvidenciaEval(idSolicitud: number): void {
    this.evidenciaCarga = "cargando";
    this.cdr.detectChanges();
    this.evidencias.consultarEvidencias(ORIGEN_SOLICITUD_IA_EVAL, idSolicitud).subscribe({
      next: (res: any) => {
        if (!this.esLaAbierta(idSolicitud)) {
          return;
        }
        // Si el servidor la niega, consultar.do contesta 200 con el cuerpo
        // VACÍO (el error va en los encabezados): sin lista es falla, no
        // "no hay evidencia".
        if (!res || res.codigoError || !Array.isArray(res.evidencias)) {
          this.evidenciaCarga = "falla";
        } else {
          this.evidenciasEval = res.evidencias;
          this.evidenciaCarga = "listo";
        }
        this.cdr.detectChanges();
      },
      error: () => {
        if (!this.esLaAbierta(idSolicitud)) {
          return;
        }
        this.evidenciaCarga = "falla";
        this.cdr.detectChanges();
      },
    });
  }

  reintentaEvidencia(): void {
    if (this.abierta && this.abierta.idSolicitudIa != null) {
      this.cargaEvidenciaEval(this.abierta.idSolicitudIa);
    }
  }

  /**
   * Subir uno o varios archivos. Uno por uno y en orden: si uno falla, los
   * demás siguen y al final se dice cuál no entró. Después se vuelve a
   * pedir la lista y la historia, que el servidor ya anotó.
   */
  subeEvidencia(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const archivos = Array.from(input.files || []);
    // Vaciar ya: volver a elegir el MISMO archivo tiene que disparar change.
    input.value = "";
    const s = this.abierta;
    if (!archivos.length || !s || s.idSolicitudIa == null || !this.puedeSubirEvidencia || this.subiendoEvidencia) {
      return;
    }
    const { buenos, malos } = this.filtraEvidencia(archivos);
    if (!buenos.length) {
      this.avisa(malos.join("\n"), "warning");
      return;
    }
    const id = s.idSolicitudIa;
    this.subeArchivosEvidencia(id, buenos, (fallidos, motivos) => {
      if (this.esLaAbierta(id)) {
        this.cargaEvidenciaEval(id);
        this.recargaDetalle();
      }
      const todos = malos.concat(motivos);
      if (todos.length) {
        this.avisa("No se subieron:\n" + todos.join("\n"), "warning");
      } else {
        const n = buenos.length - fallidos.length;
        this.avisa(n === 1 ? "Evidencia subida." : `${n} archivos de evidencia subidos.`, "success");
      }
    });
  }

  /** Separa lo que el almacén acepta (tipo y tope de 10 MB) de lo que no, con el porqué. */
  private filtraEvidencia(archivos: File[]): { buenos: File[]; malos: string[] } {
    const permitidas = this.EVIDENCIA_EXT.split(",");
    const malos: string[] = [];
    const buenos = archivos.filter((f) => {
      const ext = "." + (f.name.split(".").pop() || "").toLowerCase();
      if (!permitidas.includes(ext)) {
        malos.push(`${f.name}: ese tipo de archivo no se acepta`);
        return false;
      }
      if (f.size > this.EVIDENCIA_MAX) {
        malos.push(`${f.name}: pesa más de 10 MB`);
        return false;
      }
      return true;
    });
    return { buenos, malos };
  }

  /**
   * Sube archivos como evidencia de la evaluación, uno por uno y en orden:
   * si uno falla, los demás siguen. Al final dice cuáles no entraron (los
   * File, para poder reintentarlos, y el porqué de cada uno).
   */
  private subeArchivosEvidencia(
    id: number,
    archivos: File[],
    alTerminar: (fallidos: File[], motivos: string[]) => void,
  ): void {
    const fallidos: File[] = [];
    const motivos: string[] = [];
    this.subiendoEn.add(id);
    this.cdr.detectChanges();
    const sube = (i: number): void => {
      if (i >= archivos.length) {
        this.subiendoEn.delete(id);
        alTerminar(fallidos, motivos);
        this.cdr.detectChanges();
        return;
      }
      const f = archivos[i];
      this.evidencias.subirEvidencia(f, ORIGEN_SOLICITUD_IA_EVAL, id, "Evidencia de la evaluación").subscribe({
        next: (res: any) => {
          if (!res || res.codigoError) {
            fallidos.push(f);
            motivos.push(`${f.name}: ${(res && res.mensajeError) || "el servidor no lo aceptó"}`);
          }
          sube(i + 1);
        },
        error: () => {
          fallidos.push(f);
          motivos.push(`${f.name}: no se pudo conectar con el servidor`);
          sube(i + 1);
        },
      });
    };
    sube(0);
  }

  /* ---------- Adjuntar evidencia desde la caja de comentar ----------
     Pedido de Norman (23/09/2026): «en comentarios, ahí yo puedo subir
     evidencia de lo que evalué». Los archivos se eligen con el clip, se ven
     dentro de la caja y se suben al ENVIAR, junto con el comentario: el
     texto dice qué se revisó y los archivos lo sustentan. Quedan como
     evidencia de la evaluación (la misma lista del apartado de la
     izquierda), y sólo los ve el clip quien atiende la solicitud. */

  /** Los archivos elegidos en la caja de comentar, que se suben al enviar. */
  adjuntos: File[] = [];

  agregaAdjuntos(ev: Event): void {
    const input = ev.target as HTMLInputElement;
    const archivos = Array.from(input.files || []);
    input.value = "";
    if (!archivos.length || !this.puedeSubirEvidencia) {
      return;
    }
    const { buenos, malos } = this.filtraEvidencia(archivos);
    // El mismo archivo dos veces no: se reconoce por nombre, tamaño y fecha.
    const clave = (f: File) => `${f.name}|${f.size}|${f.lastModified}`;
    const ya = new Set(this.adjuntos.map(clave));
    this.adjuntos = this.adjuntos.concat(buenos.filter((f) => !ya.has(clave(f))));
    if (malos.length) {
      this.avisa(malos.join("\n"), "warning");
    }
    this.cdr.detectChanges();
  }

  quitaAdjunto(i: number): void {
    this.adjuntos = this.adjuntos.filter((_, k) => k !== i);
    this.cdr.detectChanges();
  }

  /** Quitar una evidencia: se confirma, y queda anotado en la historia. */
  quitaEvidencia(e: MatrizEvidencia): void {
    const s = this.abierta;
    if (!s || s.idSolicitudIa == null || e.idEvidencia == null || !this.puedeSubirEvidencia) {
      return;
    }
    const id = s.idSolicitudIa;
    Swal.fire({
      title: "¿Quitar esta evidencia?",
      text: `«${e.nombreArchivo || "archivo"}» deja de verse para quien pidió la solicitud. Queda anotado en la historia.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Quitar",
      cancelButtonText: "Cancelar",
    }).then((r) => {
      if (!r.isConfirmed) {
        return;
      }
      this.evidencias.eliminarEvidencia(e.idEvidencia!).subscribe({
        next: (res: any) => {
          // Igual que consultar: un rechazo del servidor llega sin cuerpo.
          if (!res || res.codigoError) {
            this.avisa((res && res.mensajeError) || "No se pudo quitar la evidencia.", "error");
          }
          if (this.esLaAbierta(id)) {
            this.cargaEvidenciaEval(id);
            this.recargaDetalle();
          }
        },
        error: () => this.avisa("No se pudo conectar con el servidor.", "error"),
      });
    });
  }

  /** PDF e imágenes se abren en otra pestaña; lo demás se descarga. */
  seVeEnElNavegador(e: MatrizEvidencia): boolean {
    const n = (e.nombreArchivo || "").toLowerCase();
    return /\.(pdf|jpe?g|png|gif|webp|bmp)$/.test(n);
  }

  /** Icono por tipo de archivo. */
  iconoArchivo(e: MatrizEvidencia): string {
    const n = (e.nombreArchivo || "").toLowerCase();
    if (n.endsWith(".pdf")) {
      return "picture_as_pdf";
    }
    if (/\.(jpe?g|png|gif|webp|bmp)$/.test(n)) {
      return "image";
    }
    if (this.esExcel(e)) {
      return "table_view";
    }
    if (n.endsWith(".mp4")) {
      return "movie";
    }
    return "description";
  }

  /** Abrir un PDF o una imagen en otra pestaña, por HttpClient (con token). */
  verEvidencia(e: MatrizEvidencia): void {
    if (e.idEvidencia == null) {
      return;
    }
    if (!this.seVeEnElNavegador(e)) {
      this.descarga(e);
      return;
    }
    // La pestaña se abre YA, dentro del clic: si se abre al llegar el
    // archivo, el navegador la toma por ventana emergente y la bloquea.
    const pestana = window.open("", "_blank");
    this.evidencias.descargarEvidencia(e.idEvidencia).subscribe({
      next: (blob: Blob) => {
        // Vacío = el servidor lo rechazó (ver descarga()).
        if (!blob || blob.size === 0) {
          if (pestana) {
            pestana.close();
          }
          this.avisa("No se pudo abrir el archivo.", "error");
          return;
        }
        const url = URL.createObjectURL(blob);
        if (pestana) {
          pestana.location.href = url;
        } else {
          window.open(url, "_blank");
        }
        // Treinta segundos para que la pestaña alcance a cargarla.
        setTimeout(() => URL.revokeObjectURL(url), 30000);
      },
      error: () => {
        if (pestana) {
          pestana.close();
        }
        this.avisa("No se pudo abrir el archivo.", "error");
      },
    });
  }

  /** 1.2 MB, 350 KB... */
  tamanoLegible(bytes?: number): string {
    if (bytes == null || bytes < 0) {
      return "";
    }
    if (bytes < 1024) {
      return `${bytes} B`;
    }
    if (bytes < 1024 * 1024) {
      return `${Math.round(bytes / 1024)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  /* ================= Los archivos ================= */

  /*
   * Los Excel ya no tienen «Ver»: se descargan (pedido de Norman,
   * 23/09/2026). La vista previa que había —hoja-excel.ts, con celdas
   * combinadas— no enseñaba los formatos de Solser como se ven en Excel
   * (botones, formas, estilos), y «no se ve bien y no dice nada» era peor
   * que abrir el archivo en Excel.
   */

  /** Si es hoja de cálculo: sólo cambia el icono. */
  esExcel(a: any): boolean {
    const n = (a && a.nombreArchivo ? a.nombreArchivo : "").toLowerCase();
    return n.endsWith(".xlsx") || n.endsWith(".xls") || n.endsWith(".csv");
  }

  /** Bajar cualquier archivo. Mismo camino: por HttpClient, con token. */
  descarga(a: any): void {
    if (!a || a.idEvidencia == null) {
      return;
    }
    this.evidencias.descargarEvidencia(a.idEvidencia).subscribe({
      next: (blob: Blob) => {
        // Si el servidor rechaza, descargar.do contesta 200 SIN cuerpo (el
        // error va en los encabezados): bajaría un archivo de 0 bytes.
        if (!blob || blob.size === 0) {
          this.avisa("No se pudo descargar el archivo.", "error");
          return;
        }
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = a.nombreArchivo || "archivo";
        link.click();
        URL.revokeObjectURL(url);
      },
      error: () => this.avisa("No se pudo descargar el archivo.", "error"),
    });
  }

  /* ================= Presentación ================= */

  etiqueta(s: SolicitudIA | null): string {
    if (!s) {
      return "";
    }
    const e = ESTATUS_SIA.find((x) => x.valor === s.estatus);
    return e ? e.texto : s.estatus || "";
  }

  clase(s: SolicitudIA | null): string {
    if (!s) {
      return "";
    }
    const e = ESTATUS_SIA.find((x) => x.valor === s.estatus);
    return e ? e.clase : "";
  }

  /** El color del estatus: el mismo de su columna en el tablero. */
  colorEstatus(s: SolicitudIA | null): string {
    if (!s) {
      return "#64748b";
    }
    const e = ESTATUS_SIA.find((x) => x.valor === s.estatus);
    return e ? e.color : "#64748b";
  }

  /** El icono del estatus, para la pastilla en volumen de la cabecera. */
  iconoEstatus(s: SolicitudIA | null): string {
    if (!s) {
      return "help_outline";
    }
    const e = ESTATUS_SIA.find((x) => x.valor === s.estatus);
    return e ? e.icono : "help_outline";
  }

  /**
   * Cómo se cerró, para la tarjeta de la columna Cerradas: "no" si no se
   * autorizó, "retirada" si la retiró quien la pidió. Las dos caían en la
   * misma columna con el mismo gris (pedido de Norman, 23/09/2026: «las que
   * se retiraron en gris y otro color las que no se autorizaron»).
   */
  cierreDe(s: SolicitudIA): "no" | "retirada" | null {
    if (s.estatus === "RECHAZADA") {
      return "no";
    }
    if (s.estatus === "CANCELADA") {
      return "retirada";
    }
    return null;
  }

  /** Si todavía espera algo de alguien. */
  abiertaAun(s: SolicitudIA | null): boolean {
    return (
      !!s &&
      (s.estatus === "SOLICITADA" ||
        s.estatus === "EN_EVALUACION" ||
        s.estatus === "INFORMACION_REQUERIDA")
    );
  }

  /** El texto de cada renglón de la historia. */
  textoHistorial(h: SolicitudIAHistorial): string {
    if (h.accion === "CAMBIO_ESTATUS" && h.estatusNuevo) {
      const e = ESTATUS_SIA.find((x) => x.valor === (h.estatusNuevo as any));
      return "Pasó a " + (e ? e.texto : h.estatusNuevo);
    }
    if (h.accion === "ASIGNACION") {
      return h.comentario || "Asignación";
    }
    if (h.accion === "ALTA_INVENTARIO") {
      return "Dada de alta en el inventario de IA";
    }
    // Lo asienta el servidor al autorizar, junto con el de arriba. Sin este
    // caso salía como «Comentario», como si alguien lo hubiera escrito.
    if (h.accion === "ALTA_MATRIZ") {
      return "Dada de alta en la Matriz de Riesgos IA";
    }
    // Con el control de cambios de la matriz encendido: no entró, quedó
    // esperando que se apruebe allá. La nota dice el resto.
    if (h.accion === "PROPUESTA_MATRIZ") {
      return "Propuesta a la Matriz de Riesgos IA";
    }
    if (h.accion === "ALTA") {
      return "Solicitud creada";
    }
    if (h.accion === "ARCHIVO") {
      return h.comentario || "Movimiento de archivo";
    }
    if (h.accion === "EVIDENCIA") {
      return h.comentario || "Evidencia de la evaluación";
    }
    return "Comentario";
  }

  /**
   * La nota de un renglón, cuando dice algo más que el título.
   *
   * El servidor guarda el comentario de cada movimiento, y en los que no
   * llevan nota propia ese comentario ES el título ("Solicitud creada",
   * "Tomó la solicitud", el nombre del archivo). Pintar los dos dejaba el
   * mismo renglón escrito dos veces, una debajo de la otra; de ahí que la
   * historia se viera amontonada. Se compara sin importar mayúsculas, los
   * espacios de más ni el punto final.
   */
  notaHistorial(h: SolicitudIAHistorial): string {
    const nota = (h.comentario || "").trim();
    if (!nota) {
      return "";
    }
    // El grupo fija el corte donde empiezan los puntos y espacios del final y
    // se devuelve con "$1": con muchos puntos seguidos ya no retrocede de más.
    const pelado = (s: string) => s.toLowerCase().replace(/\s+/g, " ").replace(/(^|[^.\s])[.\s]+$/, "$1").trim();
    return pelado(nota) === pelado(this.textoHistorial(h)) ? "" : nota;
  }

  /** El color del punto de la historia: dice de un vistazo qué pasó. */
  claseHistorial(h: SolicitudIAHistorial): string {
    // Los colores son los de las columnas del tablero: lo que pasa en una
    // etapa se ve del color de esa etapa. Hasta el 23/09/2026 estas clases
    // existían pero ninguna regla de CSS las pintaba: todo salía gris.
    switch (h.accion) {
      case "ALTA":
        return "es-alta";
      case "COMENTARIO":
        return "es-com";
      case "ALTA_INVENTARIO":
      case "ALTA_MATRIZ":
        return "es-inv";
      // Ámbar, como lo que está en proceso: todavía espera a alguien.
      case "PROPUESTA_MATRIZ":
        return "es-prop";
      case "ASIGNACION":
        return "es-toma";
      case "ARCHIVO":
        return "es-arch";
      case "EVIDENCIA":
        return "es-evid";
    }
    if (h.accion === "CAMBIO_ESTATUS") {
      switch (h.estatusNuevo) {
        case "APROBADA":
          return "es-si";
        case "RECHAZADA":
        case "CANCELADA":
          return "es-no";
        case "INFORMACION_REQUERIDA":
          return "es-info";
        case "EN_EVALUACION":
          return "es-proceso";
      }
    }
    return "";
  }

  iconoHistorial(h: SolicitudIAHistorial): string {
    switch (h.accion) {
      case "ALTA":
        return "add_circle";
      case "COMENTARIO":
        return "chat_bubble";
      case "ASIGNACION":
        return "person_add";
      case "ALTA_INVENTARIO":
        return "inventory_2";
      case "ALTA_MATRIZ":
        return "grid_on";
      case "PROPUESTA_MATRIZ":
        return "pending_actions";
      case "ARCHIVO":
        return "attach_file";
      case "EVIDENCIA":
        return "task";
      default:
        return "swap_horiz";
    }
  }

  /**
   * Las iniciales de quien pide, para el avatar de la tarjeta.
   *
   * El apellido PATERNO es la tercera palabra con nombre compuesto y la
   * segunda con nombre simple: tomar la ultima daria el materno. Misma
   * regla que el calendario de permisos.
   */
  iniciales(nombre?: string): string {
    const p = (nombre || "").trim().split(/\s+/).filter((x) => x.length);
    if (!p.length) {
      return "?";
    }
    const apellido = p.length >= 4 ? p[2] : p[1];
    return (p[0][0] + (apellido ? apellido[0] : "")).toUpperCase();
  }

  /**
   * La fecha en corto para la tarjeta: "10 sep", o "10 sep 25" si es de
   * otro año. Completa ocupa un renglón entero, y en una columna de 240
   * px un renglón es caro; la fecha exacta queda en el tooltip.
   */
  fechaCorta(fecha?: string): string {
    if (!fecha) {
      return "";
    }
    const p = fecha.substring(0, 10).split("-");
    if (p.length !== 3) {
      return fecha;
    }
    const mes = MESES[Number(p[1]) - 1] || p[1];
    const dia = String(Number(p[2]));
    const anio = new Date().getFullYear();
    return Number(p[0]) === anio ? dia + " " + mes : dia + " " + mes + " " + p[0].substring(2);
  }

  trackSolicitud = (_: number, s: SolicitudIA) => s.idSolicitudIa;
  trackHistorial = (_: number, h: SolicitudIAHistorial) => h.idHistorial;

  private avisa(texto: string, icono: "success" | "error" | "warning"): void {
    Swal.fire({
      text: texto,
      icon: icono,
      timer: icono === "success" ? 2000 : undefined,
      showConfirmButton: icono !== "success",
    });
  }
}
